import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { usePrevious } from "../utils/previous-hook";
import { set } from "../store/configSlice";
import configMap from "../data/config";
import materialsMap from "../data/materials";

const SectionDisabledException = {};
const OptionDisabledException = {};

function ColorsetOptions({ part, attr, regional, materialSet, setConstrainedBy }) {
  const { t } = useTranslation();
  const config = useSelector((state) => state.config);
  const region = useSelector((state) => state.config._region);
  const selectedMaterial = useSelector((state) => state.config[part][attr]);
  const dispatch = useDispatch();

  if (!materialsMap.SETS.hasOwnProperty(materialSet)) {
    console.warn(`Material set "${materialSet}" does not exist`);
    materialSet = materialsMap.SETS.DEFAULT;
  } else {
    if (!regional) {
      materialSet = materialsMap.SETS[materialSet];
    } else {
      if (!materialsMap.SETS[materialSet].hasOwnProperty(region)) {
        console.warn(
          `Material set "${materialSet}" is missing ${region} definitios`
        );
        materialSet = materialsMap.SETS.DEFAULT;
      } else {
        materialSet = materialsMap.SETS[materialSet][region];
      }
    }
  }

  let constrainTo = false;
  if (setConstrainedBy) {
    const { part, attr, values, allowedMaterials } = setConstrainedBy;
    const constrainerValue = config[part][attr];
    const isConstrained = values.find((el) => el === constrainerValue);
    if (isConstrained) {
      constrainTo = allowedMaterials;
    }
  }

  return materialSet.map((name, ix) => {
    const mat = materialsMap.MATERIALS[name];
    if (!mat) {
      console.warn(
        `Material "${name}" in "${materialSet}" colorset does not exist`
      );
      return null;
    }

    if (constrainTo) {
      const isAllowed = constrainTo.find((el) => el === name);
      if (!isAllowed) {
        return null;
      }
    }

    return (
      <button
        key={ix}
        data-interact={`${part}: ${attr}`}
        data-item={mat.transKey}
        className={`btn btn-circle btn-swatch mr-15 ${
          selectedMaterial === name ? "selected" : ""
        } ${mat.type}`}
        title={t(`materials.${mat.transKey}`)}
        style={{ backgroundColor: mat.colorIcon }}
        onClick={() => dispatch(set({ part, attr, option: { value: name } }))}
      ></button>
    );
  });
}

function LabelOptions({ part, attr, regional, options }) {
  const { t } = useTranslation();
  const config = useSelector((state) => state.config);
  const { _country } = config;
  const selectedValue = useSelector((state) => state.config[part][attr]);
  const dispatch = useDispatch();

  options = regional ? options[config._region] : options;

  if (!options.length) {
    return null;
  }

  return options.map((option, ix) => {
    if (option.hidden) {
      return null;
    }

    if (option.hasOwnProperty("country_constraints")) {
      if (option.country_constraints.hasOwnProperty("exclude")) {
        try {
          const excludedCountries = option.country_constraints.exclude;
          const isExcluded = excludedCountries.find((el) => el === _country);
          if (isExcluded) throw OptionDisabledException;
        } catch (error) {
          if (error !== OptionDisabledException) {
            throw error;
          } else {
            return null;
          }
        }
      }
    }

    if (option.hasOwnProperty("constrained_by")) {
      try {
        option.constrained_by.forEach((constraint) => {
          const { part, attr, values } = constraint;
          const constrainerValue = config[part][attr];
          const isAllowed = values.find((el) => el === constrainerValue);
          if (!isAllowed) throw OptionDisabledException;
        });
      } catch (error) {
        if (error !== OptionDisabledException) {
          throw error;
        } else {
          return null;
        }
      }
    }

    return (
      <button
        key={ix}
        data-interact={`${part}: ${attr}`}
        data-item={option.transKey}
        className={`btn ${selectedValue === option.value ? "selected" : ""}`}
        onClick={() => dispatch(set({ part, attr, option }))}
      >
        {t(`parts:${part}.${option.transKey}`)}
      </button>
    );
  });
}

function ValueOptions({ part, attr, regional, options }) {
  const { t } = useTranslation();
  const config = useSelector((state) => state.config);
  const selectedValue = useSelector((state) => state.config[part][attr]);
  const dispatch = useDispatch();

  options = regional ? options[config._region] : options;

  return options.map((option, ix) => {
    if (option.hasOwnProperty("constrained_by")) {
      try {
        option.constrained_by.forEach((constraint) => {
          const { part, attr, values } = constraint;
          const constrainerValue = config[part][attr];
          const isAllowed = values.find((el) => el === constrainerValue);
          if (!isAllowed) throw OptionDisabledException;
        });
      } catch (error) {
        if (error !== OptionDisabledException) {
          throw error;
        } else {
          return null;
        }
      }
    }

    return (
      <button
        key={ix}
        data-interact={`${part}: ${attr}`}
        data-item={option.transKey}
        className={`btn btn-circle ${
          selectedValue === option.value ? "selected" : ""
        }`}
        onClick={() => dispatch(set({ part, attr, option }))}
      >
        {t(`parts:${part}.${option.valueTransKey}`)}
      </button>
    );
  });
}

function Section({ part, attr }) {
  const { t } = useTranslation();
  const config = useSelector((state) => state.config);
  const [expanded, setExpanded] = useState(false);
  const [scrollHeight, setScrollHeight] = useState(0);

  const section = configMap[part][attr];
  const { transKey, collapsible, optionsType, options, materialSet } = section;

  const measuredRef = useCallback(
    (node) => {
      if (collapsible && node !== null) {
        setScrollHeight(node.scrollHeight);
      }
    },
    [collapsible]
  );

  // This is used when the option is "hidden" only for a region
  // (we have an empty options array)
  if (section.regional && options && !options[config._region].length) {
    return null;
  }

  if (section.hasOwnProperty("constrained_by")) {
    try {
      section.constrained_by.forEach((constraint) => {
        const { part, attr, values } = constraint;
        const constrainerValue = config[part][attr];
        const isAllowed = values.find((el) => el === constrainerValue);
        if (!isAllowed) throw SectionDisabledException;
      });
    } catch (error) {
      if (error !== SectionDisabledException) {
        throw error;
      } else {
        return null;
      }
    }
  }

  let content;
  let layout;

  if (optionsType === "label") {
    layout = `grid grid-${section.gridSize || "full"}`;
    content = (
      <LabelOptions
        part={part}
        attr={attr}
        regional={section.regional || false}
        options={options}
      />
    );
  } else if (optionsType === "value") {
    layout = "bullets";
    content = (
      <ValueOptions
        part={part}
        attr={attr}
        regional={section.regional || false}
        options={options}
      />
    );
  } else if (optionsType === "color") {
    // Don't show section if it only has one color option
    if (
      section.regional &&
      materialsMap.SETS[materialSet][config._region]?.length === 1
    ) {
      return null;
    } else if (materialsMap.SETS[materialSet]?.length === 1) {
      return null;
    }

    layout = "bullets";
    content = (
      <ColorsetOptions
        part={part}
        attr={attr}
        regional={section.regional || false}
        materialSet={materialSet || "DEFAULT"}
        setConstrainedBy={section.set_constrained_by || false}
      />
    );
  }

  let collapsibleClass = "";
  let collapsibleStyle = {};

  if (collapsible) {
    collapsibleClass = `collapsible ${expanded ? "expanded" : ""}`;
    collapsibleStyle = {
      height: expanded ? scrollHeight + "px" : "0px",
    };
  }

  return (
    <section className={collapsibleClass}>
      <div
        className="title"
        onClick={collapsible ? () => setExpanded(!expanded) : () => {}}
      >
        {collapsible && <div className="toggle-icon collapsed"></div>}
        {t(`parts:${part}.${transKey}`)}
      </div>
      <div ref={measuredRef} className="content" style={collapsibleStyle}>
        <div className={`content-inner ${layout}`}>{content}</div>
      </div>
    </section>
  );
}

export default function ConfigFormBuilder() {
  const activePart = useSelector((state) => state.nav.activePart);
  const prevActivePart = usePrevious(activePart);
  const [activePartDelayed, setActivePartDelayed] = useState(activePart);
  const scrollArea = useRef(null);

  useEffect(() => {
    const prevPart = prevActivePart;
    const part = activePart;

    // Delay unsetting the part so out transition can play
    if (prevPart !== null && part === null) {
      const timer = setTimeout(() => {
        setActivePartDelayed(part);
      }, 500);
      return () => clearTimeout(timer);
    } else if (prevPart !== part) {
      setActivePartDelayed(part);
      setTimeout(() => {
        scrollArea.current.scrollTop = 0;
      }, 10);
    }
  }, [activePart, prevActivePart]);

  if (!activePartDelayed || !configMap.hasOwnProperty(activePartDelayed)) {
    return null;
  }

  return (
    <div ref={scrollArea} className="scrollarea">
      {Object.keys(configMap[activePartDelayed]).map((key) => {
        if (configMap[activePartDelayed][key].hidden) {
          return null;
        }
        return <Section key={key} part={activePartDelayed} attr={key} />;
      })}
    </div>
  );
}
